import {ActionIcon, Alert, Button, Group, Modal, Stack, Text, TextInput} from '@mantine/core'
import {useDebouncedValue} from '@mantine/hooks'
import {IconSearch, IconX} from '@tabler/icons-react'
import {DataTable, type DataTableColumn, type DataTableSortStatus} from 'mantine-datatable'
import {useMemo, useState} from 'react'

import type {ThirdPartyRentalCompany} from './schemas'
import {ThirdPartyRentalCompanyFormModal} from './third-party-rental-company-form-modal'
import {
    useCreateThirdPartyRentalCompany,
    useDeleteThirdPartyRentalCompany,
    useThirdPartyRentalCompanies,
    useUpdateThirdPartyRentalCompany,
} from './use-third-party-rental-companies'

function hasActiveFilters(name: string) {
    return Boolean(name.trim())
}

function filterCompanies(companies: ThirdPartyRentalCompany[], nameFilter: string) {
    const name = nameFilter.trim().toLowerCase()
    if (!name) {
        return companies
    }
    return companies.filter((company) => company.name.toLowerCase().includes(name))
}

function compareCompanies(
    left: ThirdPartyRentalCompany,
    right: ThirdPartyRentalCompany,
    columnAccessor: DataTableSortStatus<ThirdPartyRentalCompany>['columnAccessor'],
) {
    const leftValue = String(left[columnAccessor as keyof ThirdPartyRentalCompany] ?? '')
    const rightValue = String(right[columnAccessor as keyof ThirdPartyRentalCompany] ?? '')
    return leftValue.localeCompare(rightValue)
}

function sortCompanies(
    companies: ThirdPartyRentalCompany[],
    sortStatus: DataTableSortStatus<ThirdPartyRentalCompany>,
) {
    const directionMultiplier = sortStatus.direction === 'asc' ? 1 : -1
    return [...companies].sort(
        (left, right) =>
            directionMultiplier * compareCompanies(left, right, sortStatus.columnAccessor),
    )
}

function columnFilterClearButton(value: string, onClear: () => void) {
    if (!value) {
        return null
    }

    return (
        <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={onClear}>
            <IconX size={14} />
        </ActionIcon>
    )
}

export function ThirdPartyRentalCompaniesPage() {
    const [nameFilter, setNameFilter] = useState('')
    const [debouncedName] = useDebouncedValue(nameFilter, 300)
    const {data: companies = [], isLoading} = useThirdPartyRentalCompanies()
    const createCompany = useCreateThirdPartyRentalCompany()
    const updateCompany = useUpdateThirdPartyRentalCompany()
    const deleteCompany = useDeleteThirdPartyRentalCompany()
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<ThirdPartyRentalCompany>>({
        columnAccessor: 'name',
        direction: 'asc',
    })
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [editingCompany, setEditingCompany] = useState<ThirdPartyRentalCompany | null>(null)
    const [deleteTarget, setDeleteTarget] = useState<ThirdPartyRentalCompany | null>(null)
    const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null)

    const filtersActive = hasActiveFilters(debouncedName)

    const sortedCompanies = useMemo(
        () => sortCompanies(filterCompanies(companies, debouncedName), sortStatus),
        [companies, debouncedName, sortStatus],
    )

    async function handleDeleteCompany() {
        if (!deleteTarget) {
            return
        }

        setDeleteErrorMessage(null)
        try {
            await deleteCompany.mutateAsync(deleteTarget.id)
            setDeleteTarget(null)
        } catch (error) {
            setDeleteErrorMessage(
                error instanceof Error ? error.message : 'Failed to delete company',
            )
        }
    }

    const columns = useMemo((): DataTableColumn<ThirdPartyRentalCompany>[] => {
        return [
            {
                accessor: 'name',
                title: 'Name',
                sortable: true,
                filter: (
                    <TextInput
                        label="Company name"
                        description="Show companies whose names include the specified text"
                        placeholder="Search company names..."
                        leftSection={<IconSearch size={16} stroke={1.5} />}
                        rightSection={columnFilterClearButton(nameFilter, () => setNameFilter(''))}
                        value={nameFilter}
                        onChange={(event) => setNameFilter(event.currentTarget.value)}
                    />
                ),
                filtering: nameFilter !== '',
            },
            {
                accessor: 'id',
                title: 'Actions',
                width: 180,
                render: (row) => (
                    <Group gap="xs">
                        <Button
                            variant="subtle"
                            size="compact-sm"
                            onClick={() => setEditingCompany(row)}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="subtle"
                            color="red"
                            size="compact-sm"
                            onClick={() => {
                                setDeleteErrorMessage(null)
                                setDeleteTarget(row)
                            }}
                        >
                            Delete
                        </Button>
                    </Group>
                ),
            },
        ]
    }, [nameFilter])

    return (
        <>
            <Group justify="flex-end" mb="md">
                <Button onClick={() => setCreateModalOpen(true)}>
                    Add third-party rental company
                </Button>
            </Group>

            <DataTable
                withTableBorder
                withColumnBorders
                striped
                highlightOnHover
                textSelectionDisabled
                idAccessor="id"
                records={sortedCompanies}
                columns={columns}
                fetching={isLoading}
                minHeight={sortedCompanies.length === 0 ? 180 : undefined}
                noRecordsText={
                    filtersActive
                        ? 'No companies match your filters.'
                        : 'No third-party rental companies yet.'
                }
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
            />

            <ThirdPartyRentalCompanyFormModal
                opened={createModalOpen}
                company={null}
                onClose={() => setCreateModalOpen(false)}
                onCreate={(input) => createCompany.mutateAsync(input)}
                onUpdate={(id, input) => updateCompany.mutateAsync({id, input})}
                onSaved={() => setCreateModalOpen(false)}
            />

            <ThirdPartyRentalCompanyFormModal
                opened={editingCompany != null}
                company={editingCompany}
                onClose={() => setEditingCompany(null)}
                onCreate={(input) => createCompany.mutateAsync(input)}
                onUpdate={(id, input) => updateCompany.mutateAsync({id, input})}
                onSaved={() => setEditingCompany(null)}
            />

            <Modal
                opened={deleteTarget != null}
                onClose={() => {
                    setDeleteTarget(null)
                    setDeleteErrorMessage(null)
                }}
                title="Delete company"
                centered
            >
                <Stack gap="md">
                    <Text size="sm">Delete {deleteTarget?.name}? This cannot be undone.</Text>
                    {deleteErrorMessage && (
                        <Alert color="red" title="Could not delete company">
                            {deleteErrorMessage}
                        </Alert>
                    )}
                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            onClick={() => {
                                setDeleteTarget(null)
                                setDeleteErrorMessage(null)
                            }}
                            disabled={deleteCompany.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            loading={deleteCompany.isPending}
                            onClick={() => void handleDeleteCompany()}
                        >
                            Delete company
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    )
}
